import { Inventory, InventoryDrive } from "types/inventory";
import { GridColumns, GridNoRowsOverlay, useGridApiContext } from "@mui/x-data-grid-pro";
import { useAlert } from "hooks/useAlert";
import { RevivnApiForm } from "components/Form/RevivnApiForm";
import { SubmitAwareIconButton } from "components/Form/SubmitAwareIconButton";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DeveloperBoardOutlined } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import useInventoryParts from "hooks/useInventoryParts";
import Printable from "components/Printable";
import PartsPrintableQRCodes from "components/TechnicianInventory/PartsPrintableQRCodes";
import { RevivnDataGrid } from "components/DataGrid/RevivnDataGrid";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";

function CreatePartRowAction({ row }: { row: InventoryDrive }) {
  const apiRef = useGridApiContext();
  const { alertSuccess } = useAlert();

  if (row.inventoryPartLabel) return null;
  return (
    <RevivnApiForm
      action={`/inventory_drives/${row.id}/part`}
      method="post"
      initialValues={{}}
      onSuccess={(response) => {
        const updatedItem = response.data as InventoryDrive;
        apiRef.current?.updateRows([{ ...updatedItem }]);
        alertSuccess("Part created successfully.");
      }}
    >
      <SubmitAwareIconButton color={"primary"} type="submit">
        <Tooltip title={"Remove & Create Part"} disableInteractive={true}>
          <DeveloperBoardOutlined />
        </Tooltip>
      </SubmitAwareIconButton>
    </RevivnApiForm>
  );
}

function PrintLabelRowAction({ row }: { row: InventoryDrive }) {
  const [printConfirmDialogOpen, setPrintConfirmDialogOpen] = useState(false);
  const printComponentRef = useRef<HTMLDivElement>(null);
  const { updateInventoryPart } = useInventoryParts();
  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
    onAfterPrint: () => setPrintConfirmDialogOpen(true),
  });

  function closePrintConfirmDialog() {
    setPrintConfirmDialogOpen(false);
  }

  if (!row.inventoryPart) return null;
  return (
    <>
      <IconButton color="primary" onClick={() => handlePrint()}>
        <PrintIcon />
      </IconButton>
      <Dialog open={printConfirmDialogOpen} onClose={closePrintConfirmDialog}>
        <DialogTitle>Confirm Label Print Action</DialogTitle>
        <DialogContent>
          <DialogContentText>By pressing Yes, we will mark this inventory part label as printed.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePrintConfirmDialog}>No</Button>
          <Button
            onClick={() => {
              if (!row.inventoryPart) return;
              updateInventoryPart({ ...row.inventoryPart, printedAt: new Date().toISOString() });
              closePrintConfirmDialog();
            }}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Printable>
        <PartsPrintableQRCodes selectedInventoryParts={[row.inventoryPart]} ref={printComponentRef} />
      </Printable>
    </>
  );
}

const columns: GridColumns<InventoryDrive> = [
  {
    field: "model",
    headerName: "Model",
    flex: 1,
  },
  {
    field: "serialNumber",
    headerName: "Serial Number",
    flex: 1,
  },
  {
    field: "driveType",
    headerName: "Drive Type",
    flex: 1,
    renderCell: ({ value, row }) => {
      return (
        <>
          {value} {row.interfaceType && `(${row.interfaceType})`}
        </>
      );
    },
  },
  { field: "sizeInGb", headerName: "Size (GB)", flex: 0.5, type: "number" },
  {
    field: "erasureStatus",
    headerName: "State",
    width: 125,
    type: "string",
    renderCell({ value, row }) {
      if (row.inventoryPartLabel) {
        return (
          <Tooltip
            title={
              <>
                Part Label: {row.inventoryPartLabel} <br />
                Erasure Status: {value} <br />
                Erasure At: {row.erasureAt}
              </>
            }
            disableInteractive={true}
          >
            <Chip label="Removed" />
          </Tooltip>
        );
      }

      if (!value) {
        return <Chip label="Missing Erasure Info" color="warning" />;
      }

      const color = value === "Successful" ? "success" : "error";

      return (
        <Tooltip title={`Erased At: ${row.erasureAt}`} disableInteractive={true}>
          <Chip label={value} color={color} />
        </Tooltip>
      );
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    pinnable: true,
    headerAlign: "right",
    resizable: false,
    type: "actions",
    align: "right",
    getActions: (params) => [<PrintLabelRowAction row={params.row} />, <CreatePartRowAction row={params.row} />],
  },
];

export function InventoryDrivesGrid(props: { inventory: Inventory }) {
  return (
    <RevivnDataGrid
      autoHeight={true}
      columns={columns}
      rows={props.inventory.drives || []}
      hideFooter={true}
      components={{
        Toolbar: undefined,
        NoRowsOverlay: GridNoRowsOverlay,
      }}
    />
  );
}
